import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { AlertTriangle, CheckCircle, XCircle } from 'lucide-react'
import { supabase } from '@lib/supabase/client'
import { appointmentService } from '@lib/supabase/appointment.service'
import { useProviderAuth } from '@hooks/useProviderAuth'
import AppointmentChat from '@components/web/AppointmentChat'
import Button from '@components/web/Button'
import { chatService } from '@lib/supabase/chat.service'
import { queryKeys } from '@lib/queryKeys'

export default function ProviderChat() {
  const { appointmentId } = useParams<{ appointmentId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { provider } = useProviderAuth()
  const queryClient = useQueryClient()
  const backPath = (location.state as { backPath?: string } | null)?.backPath ?? '/provider/chats'
  const [isUpdating, setIsUpdating] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  const chatMetaQuery = useQuery({
    queryKey: queryKeys.chat.providerMeta(appointmentId),
    enabled: !!appointmentId,
    queryFn: async () => {
      const apt = await appointmentService.getAppointmentById(appointmentId!)
      if (!apt) {
        return { error: 'Appointment not found.', patientId: null, providerId: null, patientName: undefined, status: undefined, medicalInfo: null }
      }
      if (apt.service_type !== 'Chat Consultation') {
        return {
          error: 'Chat is only available for Chat Consultation appointments.',
          patientId: null,
          providerId: null,
          patientName: undefined,
          status: undefined,
          medicalInfo: null,
        }
      }

      let patientName: string | undefined
      try {
        const { data } = await supabase
          .from('patients')
          .select('full_name, name')
          .eq('id', apt.patient_id)
          .single()
        patientName = data?.full_name ?? data?.name ?? undefined
      } catch {
        patientName = undefined
      }

      const medicalInfo = await appointmentService.getAppointmentMedicalInfo(appointmentId!)

      return {
        error: null,
        patientId: apt.patient_id,
        providerId: apt.provider_id,
        patientName,
        status: apt.status,
        medicalInfo,
      }
    },
  })

  const patientId = chatMetaQuery.data?.patientId ?? null
  const providerId = chatMetaQuery.data?.providerId ?? null
  const patientName = chatMetaQuery.data?.patientName
  const appointmentStatus = chatMetaQuery.data?.status
  const error = !appointmentId ? 'Appointment not found.' : chatMetaQuery.data?.error ?? null
  const isLoading = Boolean(appointmentId) && chatMetaQuery.isPending
  const normalizedAppointmentStatus = appointmentStatus?.toLowerCase()
  const isRequested = normalizedAppointmentStatus === 'requested'

  const handleAccept = async () => {
    if (!appointmentId || isUpdating) return
    setIsUpdating(true)
    setActionError(null)
    try {
      const result = await appointmentService.updateAppointmentStatus(appointmentId, 'scheduled')
      if (result.success) {
        await Promise.all([
          chatMetaQuery.refetch(),
          queryClient.invalidateQueries({ queryKey: queryKeys.chat.thread(appointmentId) }),
          queryClient.invalidateQueries({ queryKey: ['provider'] }),
        ])
      } else {
        setActionError(result.error ?? 'Failed to accept. Please try again.')
      }
    } catch {
      setActionError('Failed to accept. Please try again.')
    } finally {
      setIsUpdating(false)
    }
  }

  const handleDecline = async () => {
    if (!appointmentId || isUpdating) return
    if (!window.confirm('Are you sure you want to decline this chat request?')) return
    setIsUpdating(true)
    setActionError(null)
    try {
      const result = await appointmentService.updateAppointmentStatus(appointmentId, 'cancelled')
      if (result.success) {
        await Promise.all([
          chatMetaQuery.refetch(),
          queryClient.invalidateQueries({ queryKey: ['provider'] }),
        ])
        navigate(backPath)
      } else {
        setActionError(result.error ?? 'Failed to decline. Please try again.')
      }
    } catch {
      setActionError('Failed to decline. Please try again.')
    } finally {
      setIsUpdating(false)
    }
  }

  useEffect(() => {
    if (!appointmentId || !provider?.id) return

    void chatService.markProviderAppointmentChatAsSeen(provider.id, appointmentId)

    return () => {
      void chatService.markProviderAppointmentChatAsSeen(provider.id, appointmentId)
    }
  }, [appointmentId, provider?.id])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-sm text-muted-foreground mt-3">Loading chat…</p>
        </div>
      </div>
    )
  }

  if (error || !patientId || !providerId || !provider?.id) {
    return (
      <div className="flex items-center justify-center h-full p-6">
        <div className="text-center max-w-xs">
          <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-3">
            <AlertTriangle size={22} className="text-destructive" />
          </div>
          <p className="text-sm text-foreground font-medium">{error ?? 'Unable to load chat'}</p>
          <Button className="mt-4" variant="outline" size="sm" onClick={() => navigate('/provider/appointments')}>
            Back to Appointments
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {isRequested && (
        <div className="flex items-center justify-between gap-3 px-4 py-3 bg-amber-50 border-b border-amber-200 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <AlertTriangle size={16} className="text-amber-600 shrink-0" />
            <p className="text-sm text-amber-800 font-medium">This chat request is pending your acceptance.</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              className="text-destructive border-destructive/40 hover:bg-destructive/10 gap-1.5"
              onClick={handleDecline}
              disabled={isUpdating}
            >
              <XCircle size={14} />
              Decline
            </Button>
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 gap-1.5"
              onClick={handleAccept}
              disabled={isUpdating}
            >
              <CheckCircle size={14} />
              Accept
            </Button>
          </div>
        </div>
      )}
      {actionError && (
        <div className="px-4 py-2 bg-destructive/10 border-b border-destructive/20 shrink-0">
          <p className="text-xs text-destructive">{actionError}</p>
        </div>
      )}
      <div className="flex-1 min-h-0">
        <AppointmentChat
          appointmentId={appointmentId!}
          patientId={patientId}
          providerId={providerId}
          senderRole="provider"
          patientName={patientName}
          backPath={backPath}
          medicalInfo={chatMetaQuery.data?.medicalInfo ?? null}
        />
      </div>
    </div>
  )
}
